// Pusher event constants — sourced from Xmedical.Domain/*Events.cs

export enum Events {
  // AllergyList Events
  AllergyListAllergyListChanged = 'allergy_list_changed',

  // Appointment Events
  AppointmentAppointmentChanged = 'appointment_changed',
  AppointmentAppointmentTypesChanged = 'appointment_types_changed',
  AppointmentAppointmentFlagsChanged = 'appointment_flags_changed',

  // AppointmentNotification Events
  AppointmentNotificationAppointmentNotificationFailed = 'appointment_notification_failed',
  AppointmentNotificationAppointmentNotificationSent = 'appointment_notification_sent',
  AppointmentNotificationAppointmentReminderSent = 'appointment_reminder_sent',

  // Billing Events
  BillingBillingChanged = 'billing_changed',

  // Comment Events
  CommentCommentsChanged = 'comments_changed',
  CommentCommentUnlocked = 'comment_unlocked',

  // DiagnosisCode Events
  DiagnosisCodeDiagnosisCodeChanged = 'diagnosis_code_changed',

  // Email Events
  EmailEmailSent = 'email_sent',

  // Import Events
  ImportImportStarted = 'import_started',
  ImportImportPhaseComplete = 'import_phase_complete',
  ImportImportComplete = 'import_complete',
  ImportImportFailed = 'import_failed',
  ImportImportReset = 'import_reset',
  ImportUserSyncStarted = 'user_sync_started',
  ImportUserSyncComplete = 'user_sync_complete',
  ImportUserSyncFailed = 'user_sync_failed',

  // InformationRequest Events
  InformationRequestInformationRequestCreated = 'information_request_created',
  InformationRequestInformationRequestResponded = 'information_request_responded',

  // Medication Events
  MedicationMedicationListChanged = 'medication_list_changed',

  // MediSwitch Events
  MediSwitchImportComplete = 'mediswitch_import_complete',
  MediSwitchImportFailed = 'mediswitch_import_failed',

  // Message Events
  MessageMessageSent = 'message_sent',

  // OrganisationPracticeInvite Events
  OrganisationPracticeInviteCreated = 'organisation_invite_created',
  OrganisationPracticeInviteApproved = 'organisation_invite_approved',
  OrganisationPracticeInviteRejected = 'organisation_invite_rejected',
  OrganisationPracticeInviteCancelled = 'organisation_invite_cancelled',
  OrganisationAccessLevelChanged = 'organisation_access_level_changed',

  // Patient Events
  PatientPatientCreated = 'patient_created',
  PatientPatientUpdated = 'patient_updated',

  // PatientFlag Events
  PatientFlagFlagPatientsChanged = 'flag_patients_changed',
  PatientFlagPatientFlagsChanged = 'patient_flags_changed',

  // Practice Events
  PracticePracticeSetupCompleted = 'practice_setup_completed',
  PracticePracticePractitionerSetupCompleted = 'practice_practitioner_setup_completed',

  // PracticeSettings Events
  PracticeSettingsPracticeSettingChanged = 'practice_setting_changed',

  // PracticeUser Events
  PracticeUserUserSettingsChanged = 'user_settings_changed',

  // ProblemList Events
  ProblemListProblemListChanged = 'problem_list_changed',

  // Referral Events
  ReferralReferralCreated = 'referral_created',
  ReferralReferralMessageCreated = 'referral_message_created',
  ReferralReferralPatientSnapshotCreated = 'referral_patient_snapshot_created',
  ReferralReferralUpdated = 'referral_updated',

  // ReportGeneration Events
  ReportGenerationComplete = 'report_complete',
  ReportGenerationDeleted = 'report_deleted',
  ReportGenerationFailed = 'report_failed',
  ReportGenerationHalted = 'report_halted',
  ReportGenerationStarted = 'report_started',
  ReportGenerationUnlocked = 'report_unlocked',

  // Script Events
  ScriptScriptChanged = 'scripts_changed',

  // Transcription Events
  TranscriptionTranscriptionComplete = 'transcription_complete',
  TranscriptionTranscriptionDeleted = 'transcription_deleted',
  TranscriptionTranscriptionUpdated = 'transcription_updated',

  // TreatmentPlan Events
  TreatmentPlanTreatmentPlanActionItemChanged = 'treatment_plan_action_item_changed',
  TreatmentPlanTreatmentPlansChanged = 'treatment_plans_changed',

  // Upload Events
  UploadUploadComplete = 'upload_complete',
  UploadUploadDeleted = 'upload_deleted',
  UploadUploadStarted = 'upload_started',
  UploadUploadUpdated = 'upload_updated',

  // User Events
  UserTabsUpdated = 'tabs_updated'
}

/**
 * Payload for Events.AppointmentAppointmentFlagsChanged.
 *
 * Broadcast when a patient's flag set changes. `flagIds` is the full new
 * flag set for the patient (not a delta), so consumers can replace the
 * current flag list without reconciling add/remove operations.
 */
export interface PatientFlagSetChangedPayload {
  patientId: number;
  flagIds: number[];
}

/**
 * Payload for Events.CommentCommentUnlocked.
 *
 * Broadcast on the patient-events channel when a previously-locked comment
 * is rolled back to draft. Consumers should clear the locked flag for the
 * matching comment so the rest of the UI re-renders without the lock badge.
 */
export interface CommentUnlockedPayload {
  commentId: number;
  type: number;
}

/**
 * Payload for Events.ReportGenerationUnlocked.
 *
 * Broadcast on the patient-events channel when a previously-locked report
 * is rolled back to draft.
 */
export interface ReportUnlockedPayload {
  reportId: number;
}
